import "./routing-guide-layout.css";

import { useMemo } from "react";
import { useParams } from "react-router";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import { RoutingGuideGrid } from "../components/grid/index.tsx";
import { RoutingGuideModal } from "../components/modal/index.tsx";
import { initialGuide } from "../components/modal/utils/constants.ts";
import { RoutingGuideActionButtons } from "../components/routing-guide-action-buttons/routing-guide-action-buttons.tsx";
import { RoutingGuideHeader } from "../components/routing-guide-header/routing-guide-header.tsx";
import { SimulateQuoteModal } from "../components/simulate/modal/index.tsx";
import { useGetCustomerRoutingGuides } from "../hooks/use-get-customer-routing-guides.ts";
import { useRoutingGuideLayout } from "../hooks/use-routing-guide-layout.ts";

export const CustomerLayout = () => {
	const { customerId: urlCustomerId } = useParams();
	const newGuideCustomerId = urlCustomerId ? Number(urlCustomerId) : null;
	const { isInternal } = useDeploymentType();
	const { accountRoles } = useIsAuthenticated();
	const canEdit = isInternal && accountRoles.includes("LTLAdmin");

	const {
		activeCustomer,
		isCustomerLoading,
		activeGuideId,
		showRoutingGuideModal,
		handleOpenEditModal,
		onClose,
		handleCustomerChange,
		handleAddNew,
		requestDelete,
		confirmDelete,
		cancelDelete,
		pendingDeleteId,
		showSimulateModal,
		handleOpenSimulateModal,
		handleCloseSimulateModal,
	} = useRoutingGuideLayout();

	const customerId = activeCustomer?.id;

	const {
		customerRoutingGuides,
		customerRoutingGuidesIsLoading,
		customerRoutingGuidesIsFetching,
	} = useGetCustomerRoutingGuides(customerId, isInternal);

	const pendingDeleteGuide = useMemo(
		() =>
			pendingDeleteId === null
				? undefined
				: (customerRoutingGuides ?? []).find((g) => g.id === pendingDeleteId),
		[pendingDeleteId, customerRoutingGuides],
	);

	const selectedGuide = useMemo(() => {
		const found = (customerRoutingGuides ?? []).find(
			(guide) => guide.id === activeGuideId,
		);
		if (found) return found;
		if (activeGuideId === null) return null;
		return {
			...initialGuide,
			id: activeGuideId,
			customerId: newGuideCustomerId,
		};
	}, [customerRoutingGuides, activeGuideId, newGuideCustomerId]);

	return (
		<TraxPageLayout>
			<SimulateQuoteModal
				forceExternal
				customer={activeCustomer}
				handleClose={handleCloseSimulateModal}
				showSimulateModal={showSimulateModal}
			/>
			<TraxPageHeader title={"Routing Guide"} />
			<div className={"routing-guide-layout-content"}>
				<RoutingGuideHeader
					onCustomerChange={handleCustomerChange}
					selectedCustomer={activeCustomer}
				/>
				{isCustomerLoading ? (
					<LoadingSpinner />
				) : (
					<div className={"routing-guide-layout-grid-section"}>
						<div className={"routing-guide-layout-grid-wrapper"}>
							<RoutingGuideGrid
								canEdit={canEdit}
								dataState={activeCustomer ? (customerRoutingGuides ?? []) : []}
								isCustomerLayout={true}
								isLoading={
									customerRoutingGuidesIsLoading ||
									customerRoutingGuidesIsFetching
								}
								onHandleDelete={requestDelete}
								onHandleOpenEditModal={handleOpenEditModal}
							/>
						</div>
						{canEdit ? (
							<RoutingGuideActionButtons
								addNewLabel={"Add New Rule Set"}
								onAddNew={handleAddNew}
								onOpenSimulateModal={handleOpenSimulateModal}
							/>
						) : null}
					</div>
				)}
			</div>
			{showRoutingGuideModal && canEdit ? (
				<RoutingGuideModal onClose={onClose} routingGuide={selectedGuide} />
			) : null}
			{pendingDeleteId === null ? null : (
				<TraxModal
					showConfirmationButtons
					cancelText={"Cancel"}
					confirmText={"Delete"}
					onCancel={cancelDelete}
					onClose={cancelDelete}
					onConfirm={confirmDelete}
					title={"Delete Routing Guide"}
					type={"confirmation"}
				>
					Are you sure you want to delete{" "}
					<strong>{pendingDeleteGuide?.name ?? "this routing guide"}</strong>?
					This action cannot be undone.
				</TraxModal>
			)}
		</TraxPageLayout>
	);
};
